/**
 * Backoffice CRM: Families (#1809). Finds a household by its ID, or by a member's email or user ID, and shows its
 * summary and subscription above the family card the user view also shows (familyCard.js), Household tab first:
 * each member's email, role and last device (#1794), with a Look up that opens their user view.
 */

import { BADGE, BADGE_DOT, BUTTON, CARD } from "./ui/classes.js";
import { icon } from "./ui/icons.js";
import { escapeHTML, familyMessageHTML } from "./familyDetailsPanel.js";
import { createFamilyCard } from "./familyCard.js";
import { bindFamilyImportDialog } from "./familyImport.js";
import { bindFamilyRecipeDrawer } from "./familyRecipe.js";
import { bindFamilyImportBatchDrawer } from "./familyImportBatch.js";
import { autoRenewOff, offerText } from "./utils/subscriptionOffer.js";

let ctx = null;
// Which search is current, so a slow one that lands after a newer one draws nothing.
let searchSeq = 0;
// Who the search found the family through ("Found through ada@example.com"), shown in the summary.
let familySummaryVia = null;

const familyCard = createFamilyCard({
  containerId: "familyCardContainer",
  getContext: () => ctx,
  // The page is about the household, not one member: nobody is "This user", and imports ask whom they are for.
  getCurrentUid: () => null,
  onLookupUser: (uid) => { window.location.hash = `#users?uid=${encodeURIComponent(uid)}`; },
  onLoaded: (details) => drawSummary(details),
  defaultTab: "household"
});

export async function loadFamiliesTemplate() {
  const container = document.getElementById("sectionFamiliesContainer");
  if (!container) return;
  try {
    const res = await fetch("templates/families.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load families.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the families template: ${escapeHTML(err.message)}</div>`;
  }
}

/** Binds the search; reads nothing until an admin searches (app.js opens #families?id= once one is signed in). */
export function initFamiliesController(context) {
  ctx = context;
  bindFamilyImportDialog(context);
  bindFamilyRecipeDrawer(context);
  bindFamilyImportBatchDrawer(context);
  const form = document.getElementById("familySearchForm");
  const input = document.getElementById("familySearchInput");
  const clearBtn = document.getElementById("familySearchClearBtn");
  if (form && input) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const term = input.value.trim();
      if (!term) {
        ctx.showToast("Enter a family ID, or a member's email or user ID.", "info");
        return;
      }
      performFamilySearch(term);
    });
    input.addEventListener("input", () => {
      if (clearBtn) clearBtn.style.display = input.value.trim() ? "inline-flex" : "none";
    });
  }
  clearBtn?.addEventListener("click", () => {
    if (input) input.value = "";
    clearBtn.style.display = "none";
    resetFamilySearch();
  });
}

/** `#families?id=<familyId>`: the family to open, or null. */
export function familyIdFromHash(hash) {
  const [section, queryString] = String(hash || "").replace("#", "").split("?");
  if (section.toLowerCase() !== "families" || !queryString) return null;
  return new URLSearchParams(queryString).get("id")?.trim() || null;
}

/** The link the user card's Family ID opens. */
export function familyHref(familyId) {
  return `#families?id=${encodeURIComponent(familyId)}`;
}

/** Opens the family in the address, as a search for it. */
export function openFamilyFromHash() {
  const familyId = familyIdFromHash(window.location.hash);
  if (!familyId) return;
  const input = document.getElementById("familySearchInput");
  const clearBtn = document.getElementById("familySearchClearBtn");
  if (input) input.value = familyId;
  if (clearBtn) clearBtn.style.display = "inline-flex";
  performFamilySearch(familyId);
}

export function resetFamilySearch() {
  searchSeq += 1;
  familyCard.reset();
  const area = document.getElementById("familySearchResultArea");
  if (!area) return;
  area.innerHTML = `
    <div class="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-white/15">
      ${icon("home", "mx-auto size-12 text-gray-400 dark:text-gray-500")}
      <h3 class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">Search for a family</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Its subscription, members, plan, groceries, recipes and imports show here.</p>
    </div>`;
}

/**
 * Which family a search means: the term as a family ID first (unless it is an email), then as a member's email or user
 * ID through adminLookupUser, whose family is their family_id, or the family under their own ID.
 * @returns {Promise<{familyId: string, details: object|null, via: string|null}>}
 */
export async function resolveFamily(term, call) {
  if (!term.includes("@")) {
    const res = await call("adminGetFamilyDetails", { familyId: term });
    if (res?.found) return { familyId: term, details: res, via: null };
  }
  const user = await call("adminLookupUser", { identifier: term });
  if (!user?.found) return { familyId: term, details: null, via: null };
  return { familyId: user.family_id || user.uid, details: null, via: user.email || user.uid };
}

export async function performFamilySearch(term) {
  const seq = ++searchSeq;
  const area = document.getElementById("familySearchResultArea");
  if (!area) return;
  area.innerHTML = `
    <div class="${CARD.panel} px-6 py-10 text-center">
      <span class="mx-auto mb-3 block size-6 animate-spin rounded-full border-2 border-gray-300 border-t-paprika-600 dark:border-white/20 dark:border-t-paprika-400"></span>
      <p class="text-sm font-medium text-gray-900 dark:text-white">Searching for “${escapeHTML(term)}”…</p>
    </div>`;

  const { functions, httpsCallable } = ctx;
  const call = async (name, data) => (await httpsCallable(functions, name)(data)).data;
  let found;
  try {
    found = await resolveFamily(term, call);
  } catch (err) {
    console.error("❌ Family search failed:", err);
    if (seq !== searchSeq) return;
    area.innerHTML = familyMessageHTML(null, "The search failed", escapeHTML(err.message || String(err)), "red");
    return;
  }
  if (seq !== searchSeq) return;

  area.innerHTML = `
    <div id="familySummaryContainer"></div>
    <div id="familyCardContainer" class="mt-6"></div>`;
  familySummaryVia = found.via;
  if (found.details) {
    drawSummary(found.details);
    familyCard.showDetails(found.familyId, found.details);
  } else {
    familyCard.load(found.familyId);
  }
}

function drawSummary(details) {
  const el = document.getElementById("familySummaryContainer");
  if (!el) return;
  el.innerHTML = details?.found ? familySummaryHTML(details, familySummaryVia) : "";
  // The owner's ID opens their user view, as a member's Look up does.
  el.querySelector("[data-family-owner]")?.addEventListener("click", (e) => {
    window.location.hash = `#users?uid=${encodeURIComponent(e.currentTarget.dataset.familyOwner)}`;
  });
}

const dateText = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";
};

/** The household's subscription, as a badge and a line: what support needs before the details further down. */
export function subscriptionSummary(details) {
  const sub = details.subscription?.computed;
  if (!sub) {
    return details.subscription_active
      ? { badgeHTML: `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Savvy</span>`, line: "Active on the household, with no RevenueCat record (lifetime or promo)" }
      : { badgeHTML: `<span class="${BADGE.gray}">Free plan</span>`, line: "No subscription" };
  }
  const store = sub.store_label || (sub.store === "PLAY_STORE" ? "Google Play" : sub.store === "APP_STORE" ? "App Store" : sub.store) || null;
  const plan = sub.plan_name || "Savvy";
  if (sub.is_active) {
    const tone = sub.is_trial ? "blue" : "green";
    const label = sub.is_trial ? `Trial · ${plan}` : plan;
    // A trial or subscription the customer turned auto-renew off for runs to its expiry and then ends (#2485).
    const off = autoRenewOff(sub, details.subscription.event, details.subscription.raw_payload);
    const offBadge = off ? ` <span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Auto-renew off</span>` : "";
    const ends = off
      ? `auto-renew off${off.reason ? ` (${off.reason})` : ""}, ends ${dateText(sub.expires_at)}`
      : (sub.expires_at ? `renews or ends ${dateText(sub.expires_at)}` : null);
    return {
      badgeHTML: `<span class="${BADGE[tone]}"><span aria-hidden="true" class="${BADGE_DOT[tone]}"></span>${escapeHTML(label)}</span>${offBadge}`,
      line: [store, offerText(sub.offer_code), ends, sub.formatted_price].filter(Boolean).join(" · ")
    };
  }
  return {
    badgeHTML: `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Expired</span>`,
    line: [plan, store, sub.expires_at ? `ended ${dateText(sub.expires_at)}` : null].filter(Boolean).join(" · ")
  };
}

export function familySummaryHTML(details, via = null) {
  const { badgeHTML, line } = subscriptionSummary(details);
  const members = details.members || [];
  const detail = (label, valueHTML) => `
    <div class="px-4 py-3 sm:px-0">
      <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${label}</dt>
      <dd class="mt-1 text-sm text-gray-900 dark:text-white">${valueHTML}</dd>
    </div>`;
  return `
    <div class="${CARD.panel}">
      <div class="flex flex-wrap items-start justify-between gap-4 px-4 py-5 sm:px-6">
        <div class="flex min-w-0 items-center gap-4">
          <span class="flex size-12 flex-none items-center justify-center rounded-full bg-paprika-50 text-paprika-700 dark:bg-paprika-500/15 dark:text-paprika-300">${icon("home", "size-6")}</span>
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold text-gray-900 dark:text-white">${escapeHTML(details.name || "Unnamed family")}</h3>
            <p class="truncate text-sm text-gray-500 dark:text-gray-400">${via ? `Found through ${escapeHTML(via)}` : `${members.length} account${members.length === 1 ? "" : "s"}`}</p>
          </div>
        </div>
        <div class="flex flex-wrap gap-1.5">${badgeHTML}</div>
      </div>
      <dl class="grid grid-cols-1 border-t border-gray-100 px-4 sm:grid-cols-3 sm:gap-x-6 sm:px-6 dark:border-white/5">
        ${detail("Family ID", `<span class="font-mono text-xs break-all">${escapeHTML(details.family_id)}</span>`)}
        ${detail("Created", escapeHTML(dateText(details.created_at)))}
        ${detail("Accounts", `${members.length}${details.primary_user_id ? ` · owner <button type="button" data-family-owner="${escapeHTML(details.primary_user_id)}" class="${BUTTON.link} font-mono text-xs">${escapeHTML(details.primary_user_id)}</button>` : ""}`)}
        <div class="sm:col-span-3">${detail("Subscription", escapeHTML(line))}</div>
      </dl>
    </div>`;
}
